import { useTranslation } from "react-i18next";
import { useMarketingPreferences } from "@features/customerDetails/marketingPreferences/useMarketingPreferences";
import type { UseFormMethods } from "react-hook-form";
import { CheckboxWrapper } from "@common/CheckboxWrapper/CheckboxWrapper";
import { Switch } from "@common/Switch";
import { EPermissions, ICustomerDetailsForm } from "@features/customerDetails/ICustomerDetailsForm";
import { PERMISSIONS_TRADUCTION } from "@constants";
import { Collapse, Stack, Typography } from "@mui/material";
import { useStyles } from "./style";

interface IParams {
    formMethods: UseFormMethods<ICustomerDetailsForm>;
}

export const MarketingPreferences = (props: IParams) => {
    const { formMethods } = props;
    const { control, watch } = formMethods;
    const { allowThirdParty, noLongerCustomer, activeCustomer } = watch("contactPermissions");
    const { t } = useTranslation("marketingPreferences");
    const { classes } = useStyles();

    const {
        isExistingCustomerCollapsableOpened,
        isNoLongerCustomerCollapsableOpened,
        isAllowThirdPartyCollapsableOpened,
        handleExistingCustomerToggleChange,
        handleNoLongerCustomerToggleChange,
        handleAllowThirdPartyToggleChange,
    } = useMarketingPreferences({ formMethods });

    return (
        <Stack spacing={1.5}>
            <Typography color="secondary" variant="normal">
                {t("description")}
            </Typography>
            <div className={classes.containerMarketingPreferences}>
                <div className={classes.itemMarketingPreferences}>
                    <div className={classes.collapsableTop}>
                        <Switch
                            checked={isExistingCustomerCollapsableOpened}
                            dataTestId="toggle-existing-customer-choice"
                            id="toggle-existing-customer-choice"
                            label={t("existingCustomer")}
                            labelAlign="left"
                            labelColor="secondary"
                            onChange={handleExistingCustomerToggleChange}
                        />
                    </div>
                    <Collapse in={isExistingCustomerCollapsableOpened}>
                        <div className={classes.collapsableChild}>
                            {Object.entries(activeCustomer).map(([key, value]) => (
                                <CheckboxWrapper
                                    color="secondary"
                                    control={control}
                                    isRequired={value}
                                    key={`active-customer-${key}`}
                                    label={PERMISSIONS_TRADUCTION[key as keyof typeof EPermissions]}
                                    name={`contactPermissions.activeCustomer.${key}`}
                                />
                            ))}
                        </div>
                    </Collapse>
                </div>
                <div className={classes.itemMarketingPreferences}>
                    <div className={classes.collapsableTop}>
                        <Switch
                            checked={isNoLongerCustomerCollapsableOpened}
                            dataTestId="toggle-no-longer-customer-choice"
                            id="toggle-no-longer-customer-choice"
                            label={t("noLongerCustomer")}
                            labelAlign="left"
                            labelColor="secondary"
                            onChange={handleNoLongerCustomerToggleChange}
                        />
                    </div>
                    <Collapse in={isNoLongerCustomerCollapsableOpened}>
                        <div className={classes.collapsableChild}>
                            {Object.entries(noLongerCustomer).map(([key, value]) => (
                                <CheckboxWrapper
                                    color="secondary"
                                    control={control}
                                    isRequired={value}
                                    key={`no-longer-${key}`}
                                    label={PERMISSIONS_TRADUCTION[key as keyof typeof EPermissions]}
                                    name={`contactPermissions.noLongerCustomer.${key}`}
                                />
                            ))}
                        </div>
                    </Collapse>
                </div>
                <div className={classes.itemMarketingPreferences}>
                    <div className={classes.collapsableTop}>
                        <Switch
                            checked={isAllowThirdPartyCollapsableOpened}
                            dataTestId="toggle-third-party-choice"
                            id="toggle-third-party-choice"
                            label={t("thirdParty")}
                            labelAlign="left"
                            labelColor="secondary"
                            onChange={handleAllowThirdPartyToggleChange}
                        />
                    </div>
                    <Collapse in={isAllowThirdPartyCollapsableOpened}>
                        <div className={classes.collapsableChild}>
                            {Object.entries(allowThirdParty).map(([key, value]) => (
                                <CheckboxWrapper
                                    color="secondary"
                                    control={control}
                                    isRequired={value}
                                    key={`allow-third-${key}`}
                                    label={PERMISSIONS_TRADUCTION[key as keyof typeof EPermissions]}
                                    name={`contactPermissions.allowThirdParty.${key}`}
                                />
                            ))}
                        </div>
                    </Collapse>
                </div>
            </div>
        </Stack>
    );
};
